import { Alert } from "@mui/material";
import dayjs from "dayjs";
import { useIntl } from "react-intl";

import {
  DetailsList,
  DetailsListItem,
} from "@/features/trips/components/DetailsList";
import { ImageCarousel } from "@/features/trips/components/ImageCarousel";
import { RoomList } from "@/features/trips/components/RoomList";
import type { TripBooking } from "@/features/trips/types/types";

type Props = {
  trip: TripBooking;
};

export function OverviewCard({ trip }: Props) {
  const intl = useIntl();

  return (
    <div className="rounded-3xl bg-white p-1">
      <ImageCarousel
        imagesUrls={trip.package.imageUrls || []}
        imageRatio="3/2"
      />

      <div className="p-3">
        <div className="mb-1 text-xl font-medium text-text-title">
          {trip.package.name}
        </div>
        <div className="text-sm text-text-tertiary">
          {trip.package.subtitle}
        </div>

        <Alert
          severity="info"
          sx={{
            my: "calc(var(--spacing)*3)",
          }}
        >
          <div className="mb-1 text-sm font-medium text-black">
            {intl.formatMessage({
              id: "trips.bookingInfo.title",
              defaultMessage:
                "Full trip details will be shared after confirmation",
            })}
          </div>
          <div className="text-sm text-text-placeholder">
            {intl.formatMessage({
              id: "trips.bookingInfo.content",
              defaultMessage:
                "Our team will contact you with the flight & hotel details, and other trip information or requirements after confirmation",
            })}
          </div>
        </Alert>

        <DetailsList>
          <DetailsListItem
            title={intl.formatMessage({
              id: "trips.bookedOn",
              defaultMessage: "Booked on",
            })}
            value={dayjs(trip.bookedAt).format("D MMMM, YYYY")}
          />

          {/* to add spacing */}
          <DetailsListItem title="" value="" />

          <DetailsListItem
            title={intl.formatMessage({
              id: "trips.travelStartDate",
              defaultMessage: "Travel start date",
            })}
            value={dayjs(trip.startDate).format("D MMMM, YYYY")}
          />

          <DetailsListItem
            title={intl.formatMessage({
              id: "trips.travelEndDate",
              defaultMessage: "Travel end date",
            })}
            value={dayjs(trip.startDate)
              .add(trip.package.durationInDays, "day")
              .format("D MMMM, YYYY")}
          />

          <DetailsListItem
            title={intl.formatMessage({
              id: "trips.travelLength",
              defaultMessage: "Travel length",
            })}
            value={intl.formatMessage(
              {
                id: "trips.duration",
                defaultMessage:
                  "{nights} {nights, plural, one {night} other {nights}} / {days} {days, plural, one {day} other {days}}",
              },
              {
                nights: trip.package.durationInDays - 1,
                days: trip.package.durationInDays,
              },
            )}
          />

          <DetailsListItem
            title={intl.formatMessage({
              id: "trips.flightOperator",
              defaultMessage: "Flight operator",
            })}
            value={trip.package.airline}
          />

          <DetailsListItem
            title={intl.formatMessage({
              id: "trips.cities",
              defaultMessage: "Cities",
            })}
            value={trip.package.cities}
          />

          <DetailsListItem
            title={intl.formatMessage({
              id: "trips.countries",
              defaultMessage: "Countries",
            })}
            value={trip.package.countries.map((c) => c.name).join(", ")}
          />
        </DetailsList>

        <div className="mt-3 mb-1 text-sm text-text-disabled-primary">
          {intl.formatMessage({
            id: "trips.tripDetails.rooms",
            defaultMessage: "Booked rooms",
          })}
        </div>
        <RoomList
          rooms={{
            adultInDoubleRoom: trip.numberOfAdultsDoubleRoom,
            adultInSingleRoom: trip.numberOfAdultsSingleRoom,
            childWithBed: trip.numberOfChildrenWithBed,
            childWithoutBed: trip.numberOfChildrenWithoutBed,
            infant: trip.numberOfInfants,
          }}
        />

        <div className="mt-5 text-sm">{trip.package.description}</div>
      </div>
    </div>
  );
}
